import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Navbar.css';
import { menuData } from '../data/data';

const Navbar = ({ onCategorySelect, onResetFilter, cartCount = 0 }) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check if we're on homepage
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      if (scrollPosition > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDropdownEnter = (category) => {
    setActiveDropdown(category);
  };

  const handleDropdownLeave = () => {
    setActiveDropdown(null);
  };

  const handleCategoryClick = (category, subcategory) => {
    onCategorySelect(category, subcategory);
    setActiveDropdown(null);
    setMenuOpen(false);
  };

  const handleLogoClick = () => {
    onResetFilter();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Check if current active dropdown has subcategories
  const hasSubcategories = activeDropdown && menuData[activeDropdown] && menuData[activeDropdown].sections && menuData[activeDropdown].sections.length > 0;

  return (
    <>
      <nav className={`navbar ${scrolled || !isHomePage ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <div className="nav-left">
            <button className="menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
              <span>Menu</span>
            </button>
          </div>

          <div className="nav-center">
            <Link to="/" className="logo" onClick={handleLogoClick}>
              <img src="/mega.png" alt="MegaGenie" className="logo-image" />
              <span className="logo-text">MEGA GENIE</span>
            </Link>
          </div>

          <div className="nav-right">
            <button className="nav-icon search-icon-right" title="Search" onClick={() => navigate('/products?search=true')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
            </button>
            <button className="nav-icon cart-icon" title="Shopping Bag" onClick={() => navigate('/cart')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <path d="M16 10a4 4 0 0 1-8 0"></path>
              </svg>
              {cartCount > 0 && <span className="cart-count">{cartCount}</span>}
            </button>
          </div>
        </div>
      </nav>

      {/* Side Menu - Moved outside navbar to avoid z-index stacking context issues */}
      <div className={`side-menu ${menuOpen ? 'active' : ''} ${activeDropdown ? 'has-category-selected' : ''}`}>
          <div className="side-menu-overlay" onClick={() => setMenuOpen(false)}></div>
          <div className="side-menu-content">
            <button className="close-menu" onClick={() => setMenuOpen(false)} aria-label="Close menu">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            
            <div className={`menu-split ${hasSubcategories ? 'has-subcategories' : ''}`}>
              {/* Left Column - Main Categories */}
              <div className="menu-main-categories">
                <div className="menu-links-top">
                  <Link to="/products" onClick={() => setMenuOpen(false)} className="menu-all-products">All Products</Link>
                  <Link to="/products?showcaseSection=new-arrivals" onClick={() => setMenuOpen(false)}>New Arrivals</Link>
                  <Link to="/products?showcaseSection=trending-now" onClick={() => setMenuOpen(false)}>Trending Now</Link>
                  <Link to="/products?showcaseSection=mega-genie-deals" onClick={() => setMenuOpen(false)}>Mega Genie Deals</Link>
                  <Link to="/products?showcaseSection=bestsellers" onClick={() => setMenuOpen(false)}>Bestsellers</Link>
                  <Link to="/products?showcaseSection=clearance-sale" onClick={() => setMenuOpen(false)}>Clearance Sale</Link>
                </div>

                <div className="menu-category">
                  <h3 
                    className={`menu-category-title ${activeDropdown === 'women' ? 'active' : ''}`}
                    onClick={() => setActiveDropdown(activeDropdown === 'women' ? null : 'women')}
                  >
                    Women
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </h3>
                </div>

                <div className="menu-category">
                  <h3 
                    className={`menu-category-title ${activeDropdown === 'men' ? 'active' : ''}`}
                    onClick={() => setActiveDropdown(activeDropdown === 'men' ? null : 'men')}
                  >
                    Men
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </h3>
                </div>

                <div className="menu-category">
                  <h3 
                    className={`menu-category-title ${activeDropdown === 'kids' ? 'active' : ''}`}
                    onClick={() => setActiveDropdown(activeDropdown === 'kids' ? null : 'kids')}
                  >
                    Kids
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </h3>
                </div>

                <div className="menu-category">
                  <h3 
                    className={`menu-category-title ${activeDropdown === 'bags' ? 'active' : ''}`}
                    onClick={() => setActiveDropdown(activeDropdown === 'bags' ? null : 'bags')}
                  >
                    Bags & Luggage
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </h3>
                </div>

                <div className="menu-category">
                  <h3 
                    className={`menu-category-title ${activeDropdown === 'beauty' ? 'active' : ''}`}
                    onClick={() => setActiveDropdown(activeDropdown === 'beauty' ? null : 'beauty')}
                  >
                    Beauty & Personal Care
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </h3>
                </div>

                <div className="menu-category">
                  <h3 
                    className={`menu-category-title ${activeDropdown === 'unisex' ? 'active' : ''}`}
                    onClick={() => setActiveDropdown(activeDropdown === 'unisex' ? null : 'unisex')}
                  >
                    Unisex
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </h3>
                </div>

                <div className="menu-links-bottom">
                  <a href="#electronics" onClick={() => setMenuOpen(false)}>Electronics & Gadgets</a>
                  <a 
                    href="#home-living" 
                    className={activeDropdown === 'home' ? 'active' : ''}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveDropdown(activeDropdown === 'home' ? null : 'home');
                    }}
                  >
                    Home & Living
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginLeft: 'auto', opacity: activeDropdown === 'home' ? 1 : 0}}>
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </a>
                  <a 
                    href="#sports" 
                    className={activeDropdown === 'sports' ? 'active' : ''}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveDropdown(activeDropdown === 'sports' ? null : 'sports');
                    }}
                  >
                    Sports & Outdoors
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginLeft: 'auto', opacity: activeDropdown === 'sports' ? 1 : 0}}>
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </a>
                </div>

                <div className="menu-footer">
                  <p className="menu-help">Can we help you?</p>
                  <a href="tel:+1866MEGAGENIE" className="menu-phone">+1.866.MEGAGENIE</a>
                </div>
              </div>

              {/* Right Column - Subcategories */}
              <div className={`menu-subcategories ${hasSubcategories ? 'active' : ''}`}>
                {activeDropdown === 'women' && (
                  <div className="subcategory-content">
                    <h4 className="subcategory-header">Women</h4>
                    {menuData.women?.sections.map((section, index) => (
                      <div key={index} className="subcategory-section">
                        <h5>{section.title}</h5>
                        <ul>
                          {section.items.map((item, itemIndex) => (
                            <li key={itemIndex}>
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleCategoryClick('Women', item.label);
                                }}
                              >
                                {item.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {activeDropdown === 'men' && (
                  <div className="subcategory-content">
                    <h4 className="subcategory-header">Men</h4>
                    {menuData.men?.sections.map((section, index) => (
                      <div key={index} className="subcategory-section">
                        <h5>{section.title}</h5>
                        <ul>
                          {section.items.map((item, itemIndex) => (
                            <li key={itemIndex}>
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleCategoryClick('Men', item.label);
                                }}
                              >
                                {item.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {activeDropdown === 'beauty' && (
                  <div className="subcategory-content">
                    <h4 className="subcategory-header">Beauty & Personal Care</h4>
                    {menuData.beauty?.sections.map((section, index) => (
                      <div key={index} className="subcategory-section">
                        <h5>{section.title}</h5>
                        <ul>
                          {section.items.map((item, itemIndex) => (
                            <li key={itemIndex}>
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleCategoryClick('Beauty & Personal Care', item.label);
                                }}
                              >
                                {item.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {activeDropdown === 'kids' && (
                  <div className="subcategory-content">
                    <h4 className="subcategory-header">Kids</h4>
                    {menuData.kids?.sections.map((section, index) => (
                      <div key={index} className="subcategory-section">
                        <h5>{section.title}</h5>
                        <ul>
                          {section.items.map((item, itemIndex) => (
                            <li key={itemIndex}>
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleCategoryClick('Kids', item.label);
                                }}
                              >
                                {item.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {activeDropdown === 'bags' && (
                  <div className="subcategory-content">
                    <h4 className="subcategory-header">Bags & Luggage</h4>
                    {menuData.bags?.sections.map((section, index) => (
                      <div key={index} className="subcategory-section">
                        <h5>{section.title}</h5>
                        <ul>
                          {section.items.map((item, itemIndex) => (
                            <li key={itemIndex}>
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleCategoryClick('Bags & Luggage', item.label);
                                }}
                              >
                                {item.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {activeDropdown === 'home' && (
                  <div className="subcategory-content">
                    <h4 className="subcategory-header">Home & Living</h4>
                    {menuData.home?.sections.map((section, index) => (
                      <div key={index} className="subcategory-section">
                        <h5>{section.title}</h5>
                        <ul>
                          {section.items.map((item, itemIndex) => (
                            <li key={itemIndex}>
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleCategoryClick('Home & Living', item.label);
                                }}
                              >
                                {item.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {activeDropdown === 'sports' && (
                  <div className="subcategory-content">
                    <h4 className="subcategory-header">Sports & Outdoors</h4>
                    {menuData.sports?.sections.map((section, index) => (
                      <div key={index} className="subcategory-section">
                        <h5>{section.title}</h5>
                        <ul>
                          {section.items.map((item, itemIndex) => (
                            <li key={itemIndex}>
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleCategoryClick('Sports & Outdoors', item.label);
                                }}
                              >
                                {item.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {activeDropdown === 'unisex' && (
                  <div className="subcategory-content">
                    <h4 className="subcategory-header">Unisex</h4>
                    {menuData.unisex?.sections.map((section, index) => (
                      <div key={index} className="subcategory-section">
                        <h5>{section.title}</h5>
                        <ul>
                          {section.items.map((item, itemIndex) => (
                            <li key={itemIndex}>
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleCategoryClick('Unisex', item.label);
                                }}
                              >
                                {item.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
    </>
  );
};

export default Navbar;
